import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  // Priority: app user (new PIN-gated flow) → advisor token → legacy public-user token.
  const t = localStorage.getItem("ex_app_token")
    || localStorage.getItem("exodus_token")
    || localStorage.getItem("ex_user_token");
  if (t && !config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

/**
 * SESSION-SCOPED AUTH.
 *
 * Requirement: user must sign in on every fresh app launch — the app must
 * never auto-suggest / auto-login the last signed-in user.
 *
 * Implementation: token is kept in localStorage (so API calls still work
 * across page navigations), but a `session_active` sentinel is kept in
 * sessionStorage. sessionStorage is destroyed by iOS/Capacitor when the
 * WebView is terminated (i.e. true app close), and by browsers on tab
 * close — so a fresh launch will find no sentinel and force logout,
 * while normal in-app navigation stays authenticated.
 */
const SESSION_KEY_ADVISOR = "exodus_session_active";
const SESSION_KEY_USER = "ex_user_session_active";
const SESSION_KEY_APP = "ex_app_session_active";

function armSession(key) {
  try { sessionStorage.setItem(key, "1"); } catch { /* private mode */ }
}
function sessionArmed(key) {
  try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
}

// On module load, purge any stale tokens whose session is not armed.
// This runs on every page load / cold app start.
(function bootstrapSessionGate() {
  try {
    if (localStorage.getItem("exodus_token") && !sessionArmed(SESSION_KEY_ADVISOR)) {
      localStorage.removeItem("exodus_token");
      localStorage.removeItem("exodus_email");
    }
    if (localStorage.getItem("ex_user_token") && !sessionArmed(SESSION_KEY_USER)) {
      localStorage.removeItem("ex_user_token");
      localStorage.removeItem("ex_user_email");
      localStorage.removeItem("ex_user_recommendation");
    }
    if (localStorage.getItem("ex_app_token") && !sessionArmed(SESSION_KEY_APP)) {
      // Keep email + name on device so we can show the PIN screen,
      // but clear the auth token so the app locks on cold start.
      localStorage.removeItem("ex_app_token");
    }
  } catch { /* ignore */ }
})();

// ═══════════════════════════════════════════════════════════════════════
//  APP USER AUTH — Client-facing mobile users (email + full name + PIN).
//  Distinct from `auth` (advisor portal) and `userAuth` (legacy public user).
// ═══════════════════════════════════════════════════════════════════════
export const appUserAuth = {
  isLoggedIn: () => !!localStorage.getItem("ex_app_token") && sessionArmed(SESSION_KEY_APP),
  hasAccountOnDevice: () => !!localStorage.getItem("ex_app_email"),
  email: () => localStorage.getItem("ex_app_email"),
  fullName: () => localStorage.getItem("ex_app_name"),

  // Called after a successful register OR pin-login on the API.
  activate: (token, email, fullName) => {
    localStorage.setItem("ex_app_token", token);
    localStorage.setItem("ex_app_email", email);
    localStorage.setItem("ex_app_name", fullName || "");
    armSession(SESSION_KEY_APP);
    window.dispatchEvent(new Event("exodus:auth"));
  },
  // Full logout: forget the account on this device.
  forget: () => {
    localStorage.removeItem("ex_app_token");
    localStorage.removeItem("ex_app_email");
    localStorage.removeItem("ex_app_name");
    try { sessionStorage.removeItem(SESSION_KEY_APP); } catch { /* ignore */ }
  },
  // Soft lock: keep the account on device but require PIN re-entry.
  lock: () => {
    localStorage.removeItem("ex_app_token");
    try { sessionStorage.removeItem(SESSION_KEY_APP); } catch { /* ignore */ }
  },
};

export const userAuth = {
  isLoggedIn: () => !!localStorage.getItem("ex_user_token") && sessionArmed(SESSION_KEY_USER),
  login: (token, email) => {
    localStorage.setItem("ex_user_token", token);
    localStorage.setItem("ex_user_email", email);
    armSession(SESSION_KEY_USER);
  },
  logout: () => {
    localStorage.removeItem("ex_user_token");
    localStorage.removeItem("ex_user_email");
    localStorage.removeItem("ex_user_recommendation");
    try { sessionStorage.removeItem(SESSION_KEY_USER); } catch {}
  },
  email: () => localStorage.getItem("ex_user_email"),
};

export const auth = {
  isLoggedIn: () => !!localStorage.getItem("exodus_token") && sessionArmed(SESSION_KEY_ADVISOR),
  login: (token, email) => {
    localStorage.setItem("exodus_token", token);
    localStorage.setItem("exodus_email", email);
    armSession(SESSION_KEY_ADVISOR);
  },
  logout: () => {
    localStorage.removeItem("exodus_token");
    localStorage.removeItem("exodus_email");
    try { sessionStorage.removeItem(SESSION_KEY_ADVISOR); } catch {}
  },
  email: () => localStorage.getItem("exodus_email"),
};
